import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import { showTicketFormUi, showTicketListUi } from "../../../../../../app/store/slices/ticket/ticket";
import { updateTicketById, deleteAllTicketById, getTicketById } from "../../../../../../app/store/slices/ticket/ticket";
import useSetupProgress from "./useSetupProgress";
import { RUPEE_SYMBOL } from "../../../../../../app/store/constant";
import { errorToast, successToastTopRight } from "../../../../../../utils/toasts/toasts";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

// Enable UTC plugin
dayjs.extend(utc)

export default function useEventDashboardTab() {

    const navigate = useNavigate();

    const dispatch = useDispatch();

    const { eventData, userInterestedEvents } = useSelector(state => state.event);
    const { checkInStaffList } = useSelector(state => state.team);
    const { tickets } = useSelector((state) => state.ticket);
    const { attendees } = useSelector((state) => state.attendeeRegistration);
    const { invitations } = useSelector((state) => state.invitation);

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const [loading, setLoading] = useState(false);
    const [showBackdrop, setShowBackdrop] = useState(false);

    const [openModal, setOpenModal] = useState(false);
    const [modalVariant, setModalVariant] = useState(''); // 'deleteTicket' | 'markAsSoldOut' | 'endSale' | 'cannotDelete'

    const [selectedTicketId, setSelectedTicketId] = useState(null);

    // Chart States
    const [timeframe, setTimeframe] = useState('daily'); // 'daily' | 'weekly' | 'monthly'
    const [showTicketSoldChart, setShowTicketSoldChart] = useState(true);
    const [showRevenueGeneratedChart, setShowRevenueGeneratedChart] = useState(true);
    const [showTotalRevenueChart, setShowTotalRevenueChart] = useState(true);
    const [hasEnoughData, setHasEnoughData] = useState(false);

    const { progress } = useSetupProgress();

    // console.log('Event Dashboard - Setup Progress:', progress);

    const menusList = [
        {
            id: 'view-ticket',
            menuName: 'View Ticket',
            onClick: () => {
                setAnchorEl(null);
                onViewTicket();
            }
        },
        {
            id: 'mark-as-sold-out',
            menuName: 'Mark as sold out',
            onClick: () => {
                setAnchorEl(null);
                setModalVariant('markAsSoldOut');
                setOpenModal(true);
            }
        },
        {
            id: 'end-sale',
            menuName: 'End Sale',
            onClick: () => {
                setAnchorEl(null);
                setModalVariant('endSale');
                setOpenModal(true);
            }
        },
        {
            id: 'delete-ticket',
            menuName: 'Delete Ticket',
            onClick: () => {
                setAnchorEl(null);

                // find the matching ticket by ticket_id by mapping over tickets
                let matched = null;
                tickets?.map(t => {
                    if (t.ticket_id === selectedTicketId) {
                        matched = t;
                    }
                    return t;
                });

                const soldQty = matched
                    ? matched.ticket_sold
                    : 0;

                // if sold quantity > 0, show cannotDelete modal, otherwise show deleteTicket modal
                setModalVariant(soldQty > 0 ? 'cannotDelete' : 'deleteTicket');
                setOpenModal(true);
            }
        }
    ]


    /**
     * Prepares statistics data for display on the dashboard.
     */
    const stats = useMemo(() => {

        const totalTicketsSold = tickets?.reduce((total, ticket) => total + (ticket.ticket_sold || 0), 0) || 0;
        const totalRevenue = tickets?.reduce((total, ticket) => total + (ticket.ticket_sold || 0) * (ticket.price || 0), 0) || 0;

        return [
            {
                id: 'tickets-sold',
                label: 'Tickets Sold',
                value: totalTicketsSold,
            },
            {
                id: 'revenue-generated',
                label: 'Revenue Generated',
                value: `${RUPEE_SYMBOL}${totalRevenue.toFixed(2)}`,
            },
            {
                id: 'income-earned',
                label: 'Income Earned',
                value: 0,
            },
            {
                id: 'interest',
                label: 'Interest',
                value: userInterestedEvents?.length > 0
                    ? userInterestedEvents.filter(e => e.entity_id === eventData?.eventId).length
                    : 0,
            }
        ];
    }, [eventData, tickets]);


    /**
     * TODO: Sample sales summary data. In a real application, this data would come from an API or database.
     * Ticket sales data includes saleDate (UTC) and ticket price.
     */

    const salesSummaryData = useMemo(() => {
        if (!Array.isArray(attendees) || attendees.length === 0) return [];

        return attendees.map(sale => {
            return {
                saleDate: dayjs(sale.booked_on, 'YYYY-MM-DD HH:mm:ss').utc().format(),
                price: parseFloat(sale.price)
            };
        });
    }, [attendees]);


    const revenueGeneratedData = useMemo(() => {
        if (!Array.isArray(attendees) || attendees.length === 0) return [];

        const groups = new Map();

        attendees.forEach(att => {
            const id = att.ticket_type_id;
            const label = att.ticket_type ?? `Unknown-${id}`;
            const price = Number(att.price) || 0;

            if (!groups.has(id)) {
                groups.set(id, { ticket_type_id: id, label: label, value: 0, revenue: 0, price: price });
            }

            const entry = groups.get(id);
            entry.value += 1;
            entry.revenue += price;
            entry.price = entry.price;
        });

        const result = Array.from(groups.values()).sort((a, b) => b.value - a.value);
        // For debugging: grouped ticket_type_ids with counts and revenue (useful for pie chart)
        // console.log('Grouped ticket types for pie chart ->', result);

        return result;
    }, [attendees])

    // console.log("Sales Summary Data -> ", salesSummaryData)


    /**
     * Generates dynamic datasets for ticket sales based on the selected timeframe (daily, weekly, monthly).
     */
    const dynamicDatasets = useMemo(() => {

        /**
         * Returns empty datasets if there is no sales data to process.
         */
        const emptyResult = { daily: [], weekly: [], monthly: [] };
        if (salesSummaryData.length === 0) return emptyResult;


        /**
         * Milliseconds in a day (used for date calculations).
         */
        const MILLISECONDS_PER_DAY = 86400000; // 24 * 60 * 60 * 1000


        /**
         * Month names for formatting dates.
         */
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];


        /**
         * Converts a number to its ordinal representation (e.g., 1 to 1st, 2 to 2nd).
         */
        function convertToOrdinal(number) {
            const suffixes = ["th", "st", "nd", "rd"];
            const mod100 = number % 100;
            return number + (suffixes[(mod100 - 20) % 10] || suffixes[mod100] || suffixes[0]);
        };


        /**
         * Formats a UTC timestamp into a local date string (e.g., "2nd Nov 2025").
         */
        function formatLocalDay(utcTimestamps) {
            const local = new Date(utcTimestamps);
            return `${convertToOrdinal(local.getDate())} ${monthNames[local.getMonth()]} ${local.getFullYear()}`;
        };


        /**
         * Formats a UTC timestamp into a month and year string (e.g., "Nov 2025").
         */
        function formatMonthLabel(utcTimestamps) {
            const local = new Date(utcTimestamps);
            return `${monthNames[local.getMonth()]} ${local.getFullYear()}`;
        };


        /**
         * Formats a week range label given the week index and start UTC timestamp (e.g., "Week 1 — 2nd Nov 2025 - 8th Nov 2025").
         */
        function formatWeekRangeLabel(weekIndex, weekStartUtcTimestamps) {
            const weekEndUtcTs = weekStartUtcTimestamps + (6 * MILLISECONDS_PER_DAY);
            const startLabel = formatLocalDay(weekStartUtcTimestamps);
            const endLabel = formatLocalDay(weekEndUtcTs);
            return `Week ${weekIndex + 1} — ${startLabel} - ${endLabel}`;
        };


        /**
         * Converts saleDate into a UTC timestamp and converts price into number
         */
        const normalizedSalesData = salesSummaryData.map(s => ({
            timestamp: (s.saleDate instanceof Date)
                ? s.saleDate.getTime()
                : new Date(s.saleDate).getTime(),
            price: Number(s.price) || 0
        }));


        /**
         * Finds the minimum and maximum timestamps from the sale date.
         */
        const minTs = Math.min(...normalizedSalesData.map(p => p.timestamp));
        const maxTs = Math.max(...normalizedSalesData.map(p => p.timestamp));


        /**
         * If there are no valid timestamps, return empty datasets.
         */
        if (minTs === Infinity) return emptyResult;


        /**
         * Helper functions to extract UTC year and month from a timestamp.
         */
        const utcYear = ts => new Date(ts).getUTCFullYear();
        const utcMonth = ts => new Date(ts).getUTCMonth();


        /**
         * First and last sale dates in UTC.
         */
        const firstUtc = new Date(minTs);
        const lastUtc = new Date(maxTs);


        /**
         * Midnight UTC of first sale’s day
         */
        const firstDayStartUtc = Date.UTC(firstUtc.getUTCFullYear(), firstUtc.getUTCMonth(), firstUtc.getUTCDate());


        /**
         * Sunday of the week that contains the first sale
         */
        const firstWeekStartUtc = firstDayStartUtc - (firstUtc.getUTCDay() * MILLISECONDS_PER_DAY);


        /**
         * A linear month index (example: Jan 2025 = 24300)
         */
        const firstMonthIndex = utcYear(minTs) * 12 + utcMonth(minTs);


        /**
         * Midnight UTC of last sale’s day
         */
        const lastDayStartUtc = Date.UTC(lastUtc.getUTCFullYear(), lastUtc.getUTCMonth(), lastUtc.getUTCDate());


        /**
         * Sunday of the week that contains the last sale
         */
        const lastWeekStartUtc = lastDayStartUtc - (lastUtc.getUTCDay() * MILLISECONDS_PER_DAY);


        /**
         * A linear month index (example: Jan 2025 = 24300)
         */
        const lastMonthIndex = utcYear(maxTs) * 12 + utcMonth(maxTs);


        /**
         * Builds aggregated sales data grouped by the specified timeframe (daily, weekly, monthly).
         */
        function buildGroupsByTimeframe(timeframe) {

            const groups = new Map();

            /**
             * Assign sales to their respective time buckets based on the selected timeframe.
             */
            for (const p of normalizedSalesData) {
                const ts = p.timestamp;


                /**
                 * Group by daily
                 */
                if (timeframe === 'daily') {

                    /**
                     * Get the UTC timestamp for the start of the day (midnight)
                     */
                    const dayStart = Date.UTC(new Date(ts).getUTCFullYear(), new Date(ts).getUTCMonth(), new Date(ts).getUTCDate());

                    /**
                     * Calculate the day index since the first day
                     */
                    const dayIndex = Math.floor((dayStart - firstDayStartUtc) / MILLISECONDS_PER_DAY);

                    /**
                     * Initialize the bucket if it doesn't exist
                     */
                    if (!groups.has(dayIndex)) groups.set(dayIndex, { tickets: 0, revenue: 0, anchor: dayStart });

                    /**
                     * Update the bucket with ticket and revenue data
                     */
                    const bucket = groups.get(dayIndex);

                    /**
                     * Accumulate tickets and revenue for the day
                     */
                    bucket.tickets += 1;
                    bucket.revenue += p.price;
                }

                /**
                 * Group by weekly
                 */
                else if (timeframe === 'weekly') {

                    /**
                     * Get the UTC timestamp for the start of the week (Sunday)
                     */
                    const dt = new Date(ts);

                    /**
                     * Calculate the week index since the first week
                     */
                    const saleWeekStart = Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()) - (dt.getUTCDay() * MILLISECONDS_PER_DAY);

                    /**
                     * Calculate the week index since the first week
                     */
                    const weekIndex = Math.floor((saleWeekStart - firstWeekStartUtc) / (7 * MILLISECONDS_PER_DAY));

                    /**
                     * Initialize the bucket if it doesn't exist
                     */
                    if (!groups.has(weekIndex)) groups.set(weekIndex, { tickets: 0, revenue: 0, anchor: saleWeekStart });

                    /**
                     * Update the bucket with ticket and revenue data
                     */
                    const bucket = groups.get(weekIndex);

                    /**
                     * Accumulate tickets and revenue for the week
                     */
                    bucket.tickets += 1;
                    bucket.revenue += p.price;
                }

                /**
                 * Group by monthly
                 */
                else {

                    /**
                     * Calculate the month index since the first month
                     */
                    const monthIndex = utcYear(ts) * 12 + utcMonth(ts) - firstMonthIndex;

                    /**
                     * Get the UTC timestamp for the sale's year and month (1st day of the month)
                     */
                    const year = utcYear(ts)
                    const month = utcMonth(ts);

                    /**
                     * Get the UTC timestamp for the start of the month (1st day)
                     */
                    const monthStart = Date.UTC(year, month, 1);

                    /**
                     * Initialize the bucket if it doesn't exist
                     */
                    if (!groups.has(monthIndex)) groups.set(monthIndex, { tickets: 0, revenue: 0, anchor: monthStart });

                    /**
                     * Update the bucket with ticket and revenue data
                     */
                    const bucket = groups.get(monthIndex);

                    /**
                     * Accumulate tickets and revenue for the month
                     */
                    bucket.tickets += 1;
                    bucket.revenue += p.price;
                }
            }


            /**
             * Fill in missing time buckets between the first and last sale to ensure continuity in the dataset.
             */
            /**
             * Fill missing daily buckets
             */
            if (timeframe === 'daily') {
                /**
                 * Calculate the last day index since the first day (eg: 0 for first day, 1 for second day, etc.)
                 */
                const lastIndex = Math.floor((lastDayStartUtc - firstDayStartUtc) / MILLISECONDS_PER_DAY);

                /**
                 * Iterate through each day index and ensure a bucket exists
                 */
                for (let i = 0; i <= lastIndex; i++) {
                    /**
                     * Initialize the bucket if it doesn't exist
                     */
                    if (!groups.has(i)) groups.set(i, { tickets: 0, revenue: 0, anchor: firstDayStartUtc + (i * MILLISECONDS_PER_DAY) });
                }
            }

            /**
             * Fill missing weekly buckets
             */
            else if (timeframe === 'weekly') {
                /**
                 * Calculate the last week index since the first week (eg: 0 for first week, 1 for second week, etc.)
                 */
                const lastWeekIdx = Math.floor((lastWeekStartUtc - firstWeekStartUtc) / (7 * MILLISECONDS_PER_DAY));

                /**
                 * Iterate through each week index and ensure a bucket exists
                 */
                for (let i = 0; i <= lastWeekIdx; i++) {

                    /**
                     * Initialize the bucket if it doesn't exist
                     */
                    if (!groups.has(i)) groups.set(i, { tickets: 0, revenue: 0, anchor: firstWeekStartUtc + (i * 7 * MILLISECONDS_PER_DAY) });
                }
            }
            /**
             * Fill missing monthly buckets
             */
            else {

                /**
                 * Calculate the last month index since the first month (eg: 0 for first month, 1 for second month, etc.)
                 * Get the first year and month from firstMonthIndex
                 */
                const lastIdx = lastMonthIndex - firstMonthIndex;
                const firstYear = Math.floor(firstMonthIndex / 12);
                const firstMonth = firstMonthIndex % 12;

                /**
                 * Iterate through each month index and ensure a bucket exists
                 */
                for (let i = 0; i <= lastIdx; i++) {

                    /**
                     * Initialize the bucket if it doesn't exist
                     */
                    if (!groups.has(i)) {

                        /**
                         * Calculate year and month for the current index
                         * Handle year rollover when month exceeds December (11)
                         * Initialize the bucket with 0 tickets and 0 revenue
                         * anchor is the UTC timestamp for the 1st day of the month
                         */
                        const totalMonth = firstMonth + i;
                        const year = firstYear + Math.floor(totalMonth / 12);
                        const month = totalMonth % 12;
                        const anchor = Date.UTC(year, month, 1);
                        groups.set(i, { tickets: 0, revenue: 0, anchor });
                    }
                }
            }


            /**
             * Construct the final result array sorted by time index with cumulative total revenue.
             */
            const indices = Array.from(groups.keys()).sort((a, b) => a - b);

            /**
             * result array to hold the final dataset
             * runningTotal to keep track of cumulative revenue
             */
            const result = [];
            let runningTotal = 0;

            /**
             * Iterate through each index to build the final dataset with cumulative revenue
             */
            for (const idx of indices) {

                /**
                 * Get the corresponding bucket from the groups map
                 * Update the running total revenue
                 * Format the period label based on the timeframe
                 * Push the final data object into the result array
                 * Return the result array
                 */
                const bucket = groups.get(idx);
                runningTotal += bucket.revenue;

                let periodLabel;

                if (timeframe === 'daily') {
                    periodLabel = formatLocalDay(bucket.anchor);
                } else if (timeframe === 'weekly') {
                    periodLabel = formatWeekRangeLabel(idx, bucket.anchor); // idx is 0-based; label function adds +1
                } else { // monthly
                    periodLabel = formatMonthLabel(bucket.anchor);
                }

                result.push({
                    period: periodLabel,
                    tickets: bucket.tickets,
                    revenue: bucket.revenue,
                    totalRevenue: runningTotal
                });
            }

            return result;

        }


        /**
         * Build datasets for daily, weekly, and monthly timeframes.
         */
        const daily = buildGroupsByTimeframe('daily')
        const weekly = buildGroupsByTimeframe('weekly')
        const monthly = buildGroupsByTimeframe('monthly')

        return {
            daily,
            weekly,
            monthly
        }

    }, [salesSummaryData]);


    /**
     * Selects the current dataset based on the selected timeframe.
     */
    const currentDataset = useMemo(() => {
        if (!dynamicDatasets) return [];
        if (timeframe === 'daily') return dynamicDatasets.daily;
        if (timeframe === 'weekly') return dynamicDatasets.weekly;
        return dynamicDatasets.monthly;
    }, [dynamicDatasets, timeframe]);


    /**
     * Handles closing of the elipsis menu.
     */
    function onElipsisClose() {
        setAnchorEl(null);
    }


    /**
     * Handles clicking on the elipsis menu for a specific ticket.
     */
    function onElipsisClick(event, ticket) {
        setAnchorEl(event.currentTarget);
        setSelectedTicketId(ticket?.ticket_id ?? null);
    }


    /**
     * Navigates to the team management page for the event.
     */
    function onManageTeam() {
        navigate(`/event/${eventData?.slug}/team`);
    }


    /**
     * @async
     * @function onDeleteTicket
     * @description Handles the deletion of a ticket.
     */
    async function onDeleteTicket() {
        try {
            setLoading(true);

            await dispatch(deleteAllTicketById([selectedTicketId])).unwrap();

            setOpenModal(false);
            setModalVariant('');
            setSelectedTicketId(null);

            successToastTopRight('Ticket deleted successfully.');
        } catch (error) {
            errorToast(error.message || 'Failed to delete ticket.');
        } finally {
            setLoading(false);
        }
    }


    /**
     * @async
     * @function onEndSaleTicket
     * @description Handles ending the sale of a ticket.
     */
    async function onEndSaleTicket() {

        try {
            setLoading(true);
            await dispatch(
                updateTicketById({
                    ticketId: selectedTicketId,
                    ticketData: {
                        sale_status: 'sale_ended'
                    }
                })
            ).unwrap();

            successToastTopRight('Ticket sale ended successfully.');
            setOpenModal(false);
            setModalVariant('');
            setSelectedTicketId(null);
        } catch (error) {
            // console.error('Error ending ticket sale:', error);
            errorToast(error.message || 'Failed to end ticket sale.');
        } finally {
            setLoading(false);
        }
    }


    /**
     * @async
     * @function onMarkAsSoldOutTicket
     * @description Handles marking a ticket as sold out.
     */
    async function onMarkAsSoldOutTicket() {

        try {
            setLoading(true);
            await dispatch(
                updateTicketById({
                    ticketId: selectedTicketId,
                    ticketData: {
                        sale_status: 'sold_out'
                    }
                })
            ).unwrap();

            successToastTopRight('Ticket marked as sold out successfully.');

            setOpenModal(false);
            setModalVariant('');
            setSelectedTicketId(null);
        } catch (error) {
            // console.error('Error marking ticket as sold out:', error);
            errorToast(error.message || 'Failed to mark ticket as sold out.');
        } finally {
            setLoading(false);
        }
    }


    /**
     * Handles adding a new ticket type.
     */
    function onNewTicketTypeAdd() {
        setShowBackdrop(true);

        setTimeout(() => {
            dispatch(showTicketFormUi({ showTicketForm: true }));
            navigate(`/event/${eventData?.slug}/tickets`);
            setShowBackdrop(false);
        }, 1500); // Simulate 1.5s process
    }


    /**
     * @async
     * @function onViewTicket
     * @description Handles viewing a ticket by fetching its details and navigating to the ticket page.
     */
    async function onViewTicket() {
        // show backdrop to simulate loading/process in the UI
        setShowBackdrop(true);

        // simulate a process (1.5s) before performing the fetch and navigation
        setTimeout(async () => {
            try {
                await dispatch(getTicketById(selectedTicketId)).unwrap();
                dispatch(showTicketFormUi({ showTicketForm: true }));
                dispatch(showTicketListUi({ showTicketList: false }));
                navigate(`/event/${eventData?.slug}/tickets`);

                setSelectedTicketId(null);
            } catch (error) {
                errorToast(error.message || 'Failed to fetch ticket.');
            } finally {
                setShowBackdrop(false);
            }
        }, 1500);
    }


    /**
     * Handles closing the modal dialog.
     * Sets the modal state back to default.
     * Sets selected ticket ID to null.
     */
    function onModalClose() {
        setOpenModal(false);
        setModalVariant('');
        setSelectedTicketId(null);
    }


    /**
     * Handles viewing all attendees by navigating to the registrations page.
     */
    function onViewAllAttendees() {
        setShowBackdrop(true);

        setTimeout(() => {
            navigate(`/event/${eventData?.slug}/registrations`);
            setShowBackdrop(false);
        }, 1500); // Simulate 1.5s process
    }


    /**
     * Handles viewing all invitations by navigating to the invitations page.
     */
    function onViewAllInvitations() {
        setShowBackdrop(true);

        setTimeout(() => {
            navigate(`/event/${eventData?.slug}/invitations`);
            setShowBackdrop(false);
        }, 1500);
    }


    /**
     * Handles changing the timeframe for data visualization.
     */
    function onTimeframeChange(newTimeframe) {
        setTimeframe(newTimeframe);
    }


    /**
     * Handles toggling the visibility of the Ticket Sold chart.
     */
    function onShowTicketSoldChart() {
        setShowTicketSoldChart(!showTicketSoldChart);
    }


    /**
     * Handles toggling the visibility of the Revenue Generated chart.
     */
    function onShowRevenueGeneratedChart() {
        setShowRevenueGeneratedChart(!showRevenueGeneratedChart);
    }


    /**
     * Handles toggling the visibility of the Total Revenue chart.
     */
    function onShowTotalRevenueChart() {
        setShowTotalRevenueChart(!showTotalRevenueChart);
    }


    /**
     * Return all necessary states and handlers for the Event Dashboard Tab.
     */
    return {
        onManageTeam,
        checkInStaff: checkInStaffList?.data || [],
        tickets: tickets,
        onNewTicketTypeAdd,
        anchorEl,
        open,
        onElipsisClick,
        onElipsisClose,
        menusList,
        loading,
        openModal,
        modalVariant,
        onModalClose,
        onDeleteTicket,
        onEndSaleTicket,
        onMarkAsSoldOutTicket,
        showBackdrop,
        onViewTicket,
        onViewAllAttendees,
        attendees,
        invitations,
        onViewAllInvitations,
        stats,
        eventData,
        progress,
        timeframe,
        onTimeframeChange,
        onShowTicketSoldChart,
        onShowRevenueGeneratedChart,
        showTicketSoldChart,
        showRevenueGeneratedChart,
        currentDataset,
        onShowTotalRevenueChart,
        showTotalRevenueChart,
        revenueGeneratedData
    }
}